/* The findings list — the left pane of the workspace.
 *
 * A rail is for scanning, so a row is dense: the plain-language claim clamped to
 * two lines, where it is, how it was established, and — once it has one — the
 * decision. No failure-mode code appears here; a code is provenance and lives in
 * layer 3 of the finding.
 *
 * Everything the rail decides — what is grouped where, what the filters leave,
 * what j and k do — is in src/routes/workspaceModel.ts, so it can be asserted
 * without a browser. */

import { useEffect, useRef, type KeyboardEvent } from 'react';
import { Link } from 'react-router-dom';

import { Button, Disclosure, EvidenceBadge, Label } from '../primitives';
import { glossaryEntries } from '../Glossary';
import { locationChip } from '../finding';
import { evidenceBadgeFor, summaryLine } from '../../data/report';
import { pendingGroups, type PendingCheckRow } from './reconcileModel';
import {
  DECISION_FILTERS,
  EVIDENCE_FILTERS,
  dispositionMark,
  keyboardOrder,
  mutedGroupLabel,
  navDeltaFor,
  nextFindingId,
  type DecisionFilter,
  type FindingGroup,
  type Progress,
} from '../../routes/workspaceModel';
import type { Disposition, EvidenceFilter, Flag } from '../../types';
import './FindingsRail.css';

export interface FindingsRailProps {
  /** Every finding, for the counts that describe the review rather than the view. */
  findings: Flag[];
  groups: FindingGroup[];
  dispositions: Record<string, Disposition>;
  progress: Progress;
  selectedId: string | null;
  onSelect(id: string): void;
  decisionFilter: DecisionFilter;
  onDecisionFilter(next: DecisionFilter): void;
  evidenceFilter: EvidenceFilter;
  onEvidenceFilter(next: EvidenceFilter): void;
  /** "Nothing found in: …", or null when every plan area holds something. */
  nothingFound: string | null;
  /**
   * Checks that have not produced findings: one that is waiting on the
   * reviewer's confirmation, one they set aside, one that could not run at all.
   * They are drawn beneath the plan areas, in their own named sections, so that
   * a check which never ran can never be mistaken for one that ran and found
   * nothing.
   */
  pending?: PendingCheckRow[];
  /**
   * The one line COPY.md puts under the findings header on the worked example.
   * Not hidden, not expandable, and absent on a real review.
   */
  provenance?: string | null;
  /**
   * "Still checking… (3 found so far)", while checks are still arriving. Null
   * once the run has finished — and null throughout for a review read back out
   * of storage, which was never mid-run.
   *
   * It sits with the progress count rather than over the list, because the
   * findings already there are decidable and the list must not read as
   * provisional.
   */
  streaming?: string | null;
  /**
   * Findings of checks the reviewer has muted. Never removed and never hidden:
   * they sit in one collapsed group at the bottom of the list, and the keyboard
   * reaches them once it is open.
   */
  muted?: Flag[];
  mutedOpen?: boolean;
  onMutedOpenChange?(open: boolean): void;
}

export default function FindingsRail({
  findings,
  groups,
  dispositions,
  progress,
  selectedId,
  onSelect,
  decisionFilter,
  onDecisionFilter,
  evidenceFilter,
  onEvidenceFilter,
  nothingFound,
  pending = [],
  provenance,
  streaming = null,
  muted = [],
  mutedOpen = false,
  onMutedOpenChange,
}: FindingsRailProps) {
  const rows = useRef(new Map<string, HTMLButtonElement>());
  const order = keyboardOrder(groups, pending, mutedOpen ? muted : []);

  /*
   * The selected row has to be visible, or the reviewer cannot tell which of
   * ten findings is the one open beside them. It is scrolled to only when it is
   * genuinely off screen: measuring first rather than trusting 'nearest' keeps
   * the list still when the row is already in view, and keeps the progress
   * count at the top of the rail on arrival. A frame is waited for so the
   * measurement is of laid-out boxes rather than of the first paint.
   */
  useEffect(() => {
    if (!selectedId) return;
    const row = rows.current.get(selectedId);
    if (!row) return;

    const frame = requestAnimationFrame(() => {
      const pane = row.closest('[data-scroll-pane]');
      if (!pane) return;
      const rowBox = row.getBoundingClientRect();
      const paneBox = pane.getBoundingClientRect();
      if (rowBox.top < paneBox.top || rowBox.bottom > paneBox.bottom) {
        row.scrollIntoView({ block: 'nearest' });
      }
    });
    return () => cancelAnimationFrame(frame);
  }, [selectedId]);

  /**
   * j / k and the arrow keys move between findings while the list has focus.
   * The row that arrives takes focus, so the selection and the focus ring never
   * describe two different rows.
   */
  const onKeyDown = (event: KeyboardEvent<HTMLElement>) => {
    const delta = navDeltaFor(event.key);
    if (delta === null) return;
    const next = nextFindingId(order, selectedId, delta);
    if (next === null) return;
    event.preventDefault();
    onSelect(next);
    rows.current.get(next)?.focus();
  };

  return (
    <nav className="fr" aria-label="Findings" onKeyDown={onKeyDown}>
      <div className="fr__head">
        <Label mono>Findings</Label>

        {/* A count and a rule. Never a percentage, never a score. */}
        <p className="fr__progress" role="status">
          {progress.label}
        </p>
        <div className="fr__meter" aria-hidden="true">
          <span
            className="fr__meter-fill"
            style={{ width: `${Math.round(progress.fraction * 100)}%` }}
          />
        </div>

        {/* What the findings are made of. Counts, never a score. */}
        <p className="fr__composition">{summaryLine(findings)}</p>

        {streaming ? (
          <p className="fr__streaming" role="status">
            {streaming}
          </p>
        ) : null}

        {provenance ? <p className="fr__provenance">{provenance}</p> : null}
      </div>

      <div className="fr__filters">
        <FilterRow
          label="Show"
          options={DECISION_FILTERS}
          value={decisionFilter}
          onChange={onDecisionFilter}
        />
        <FilterRow
          label="Evidence"
          options={EVIDENCE_FILTERS}
          value={evidenceFilter}
          onChange={onEvidenceFilter}
        />
      </div>

      {groups.length === 0 ? (
        <div className="fr__none">
          {/* Two different emptinesses. A muted check is not a filter, so
              telling a reviewer to clear their filters when what emptied the
              list was their own muting would send them looking in the wrong
              place. */}
          <p className="fr__none-line">
            {muted.length > 0 && decisionFilter === 'all' && evidenceFilter === 'all'
              ? 'Every finding here is from a check you have muted. They are in the group below.'
              : 'No findings match those two filters.'}
          </p>
          {decisionFilter === 'all' && evidenceFilter === 'all' ? null : (
            <Button
              variant="secondary"
              marker
              onClick={() => {
                onDecisionFilter('all');
                onEvidenceFilter('all');
              }}
            >
              Show every finding
            </Button>
          )}
        </div>
      ) : (
        groups.map((group) => (
          <section key={group.area} className="fr__group">
            <h2 className="fr__group-head">
              {group.label}
              <span className="fr__group-count">{group.total}</span>
            </h2>
            <ul className="fr__list">
              {group.findings.map((finding) => {
                const disposition = dispositions[finding.id];
                const chip = locationChip(finding.sectionRef, finding.page);
                const selected = finding.id === selectedId;
                return (
                  <li
                    key={finding.id}
                    className={[
                      'fr__item',
                      selected ? 'fr__item--selected' : null,
                      disposition ? `fr__item--${disposition.decision}` : null,
                    ]
                      .filter(Boolean)
                      .join(' ')}
                  >
                    <button
                      type="button"
                      className="fr__row"
                      aria-current={selected ? 'true' : undefined}
                      ref={(node) => {
                        if (node) rows.current.set(finding.id, node);
                        else rows.current.delete(finding.id);
                      }}
                      onClick={() => onSelect(finding.id)}
                    >
                      <span className="fr__claim">{finding.claim}</span>
                    </button>
                    <span className="fr__meta">
                      {chip ? <span className="fr__chip">{chip}</span> : null}
                      <EvidenceBadge
                        id={evidenceBadgeFor(finding).id}
                        className="fr__badge"
                      />
                      {disposition ? (
                        <span className="fr__decision">
                          {dispositionMark(disposition)}
                        </span>
                      ) : null}
                    </span>
                  </li>
                );
              })}
            </ul>
          </section>
        ))
      )}

      {pendingGroups(pending).map((group) => (
        <section key={group.group} className="fr__group fr__group--pending">
          <h2 className="fr__group-head">
            {group.label}
            <span className="fr__group-count">{group.rows.length}</span>
          </h2>
          <ul className="fr__list">
            {group.rows.map((row) => {
              const selected = row.id === selectedId;
              return (
                <li
                  key={row.id}
                  className={[
                    'fr__item',
                    'fr__item--pending',
                    selected ? 'fr__item--selected' : null,
                  ]
                    .filter(Boolean)
                    .join(' ')}
                >
                  <button
                    type="button"
                    className="fr__row"
                    aria-current={selected ? 'true' : undefined}
                    ref={(node) => {
                      if (node) rows.current.set(row.id, node);
                      else rows.current.delete(row.id);
                    }}
                    onClick={() => onSelect(row.id)}
                  >
                    <span className="fr__claim">{row.claim}</span>
                  </button>
                  <span className="fr__meta">
                    <span className="fr__decision">{row.mark}</span>
                  </span>
                </li>
              );
            })}
          </ul>
        </section>
      ))}

      {muted.length > 0 ? (
        <Disclosure
          className="fr__muted"
          headClassName="fr__muted-head"
          panelClassName="fr__muted-body"
          summary={mutedGroupLabel(muted.length)}
          open={mutedOpen}
          onOpenChange={onMutedOpenChange}
        >
          <p className="fr__muted-note">
            You asked Ceiling to stop surfacing these by default. They still ran
            and they are still here. Turn them back on from{' '}
            <Link to="/checks">What it checks</Link>.
          </p>
          <ul className="fr__list">
            {muted.map((finding) => {
              const disposition = dispositions[finding.id];
              const chip = locationChip(finding.sectionRef, finding.page);
              const selected = finding.id === selectedId;
              return (
                <li
                  key={finding.id}
                  className={[
                    'fr__item',
                    selected ? 'fr__item--selected' : null,
                    disposition ? `fr__item--${disposition.decision}` : null,
                  ]
                    .filter(Boolean)
                    .join(' ')}
                >
                  <button
                    type="button"
                    className="fr__row"
                    aria-current={selected ? 'true' : undefined}
                    ref={(node) => {
                      if (node) rows.current.set(finding.id, node);
                      else rows.current.delete(finding.id);
                    }}
                    onClick={() => onSelect(finding.id)}
                  >
                    <span className="fr__claim">{finding.claim}</span>
                  </button>
                  <span className="fr__meta">
                    {chip ? <span className="fr__chip">{chip}</span> : null}
                    <EvidenceBadge
                      id={evidenceBadgeFor(finding).id}
                      className="fr__badge"
                    />
                    {disposition ? (
                      <span className="fr__decision">
                        {dispositionMark(disposition)}
                      </span>
                    ) : null}
                  </span>
                </li>
              );
            })}
          </ul>
        </Disclosure>
      ) : null}

      {nothingFound ? <p className="fr__nothing-found">{nothingFound}</p> : null}

      {/* The glossary belongs where the vocabulary is being read, not on one
          example page. */}
      <Disclosure
        className="fr__terms"
        headClassName="fr__terms-head"
        panelClassName="fr__terms-body"
        summary="Terms used here"
      >
        <dl className="fr__terms-list">
          {glossaryEntries().map((entry) => (
            <div key={entry.term}>
              <dt>{entry.term}</dt>
              <dd>{entry.definition}</dd>
            </div>
          ))}
        </dl>
      </Disclosure>
    </nav>
  );
}

interface FilterRowProps<T extends string> {
  label: string;
  options: { value: T; label: string }[];
  value: T;
  onChange(next: T): void;
}

function FilterRow<T extends string>({
  label,
  options,
  value,
  onChange,
}: FilterRowProps<T>) {
  return (
    <div className="fr__filter-row" role="group" aria-label={`${label} filter`}>
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          className={`fr__filter${option.value === value ? ' fr__filter--on' : ''}`}
          aria-pressed={option.value === value}
          onClick={() => onChange(option.value)}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}
